import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Navbar scroll effect
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  // Add Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "KREAT",
      "url": siteUrl,
      "logo": `${siteUrl}/vite.svg`,
      "description": "Transformamos equipos en motores de cambio a través de talleres corporativos innovadores y personalizados",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "C/Manel Farrés, 15",
        "addressLocality": "Sant Cugat del Vallès",
        "addressRegion": "Barcelona",
        "postalCode": "08172",
        "addressCountry": "ES"
      },
      "email": "smh@silviamanzana.com",
      "sameAs": [
        "http://www.kreat.pro"
      ]
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "KREAT",
      "url": siteUrl,
      "description": "Talleres corporativos que transforman equipos en motores de cambio"
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Formación Corporativa",
      "provider": {
        "@type": "Organization",
        "name": "KREAT"
      },
      "areaServed": {
        "@type": "Country",
        "name": "España"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Talleres Corporativos",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "El Arte de Negociar Con y Sin Palabra",
              "description": "Domina la negociación no verbal: lenguaje corporal, respiración, mirada, gestualidad y el poder del vestuario profesional"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "De Empleados Pasivos a Emprendedores Corporativos",
              "description": "Fomenta la adhesión a la cultura empresarial y desarrolla el espíritu emprendedor de tu team"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "La Motivación Micro Empresarios Internos",
              "description": "Transforma a tus empleados en micro empresarios que impulsan el crecimiento desde dentro"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Creatividad Gimnasio Mental",
              "description": "Fortalece el músculo creativo de tu equipo para que sea capaz de transformar los retos en oportunidades"
            }
          }
        ]
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿Qué tipo de talleres corporativos ofrece KREAT?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "KREAT ofrece cuatro talleres principales: El Arte de Negociar Con y Sin Palabra, De Empleados Pasivos a Emprendedores Corporativos, La Motivación Micro Empresarios Internos, y Creatividad Gimnasio Mental. Todos los talleres son personalizados según las necesidades de cada empresa."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cuál es la metodología de los talleres KREAT?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nuestra metodología consta de cuatro pasos: El Rollo (introducción conceptual), Dinámica de Grupo (experiencia práctica), Conclusiones (integración de aprendizajes), y Tailoring (adaptación completa a las necesidades específicas de tu empresa)."
          }
        },
        {
          "@type": "Question",
          "name": "¿Dónde se encuentra KREAT?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "KREAT está ubicado en C/Manel Farrés, 15, 08172 Sant Cugat del Vallès, Barcelona, España."
          }
        }
      ]
    };

    // Add schemas to head
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(organizationSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(websiteSchema);
    document.head.appendChild(script2);

    const script3 = document.createElement('script');
    script3.type = 'application/ld+json';
    script3.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script3);

    const script4 = document.createElement('script');
    script4.type = 'application/ld+json';
    script4.text = JSON.stringify(faqSchema);
    document.head.appendChild(script4);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
      document.head.removeChild(script3);
      document.head.removeChild(script4);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <header>
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              KREAT
            </h1>
          </header>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            <a href="#talleres" className="text-sm hover:text-gray-600 transition cursor-pointer">Talleres</a>
            <a href="#metodologia" className="text-sm hover:text-gray-600 transition cursor-pointer">Metodología</a>
            <a href="#testimonios" className="text-sm hover:text-gray-600 transition cursor-pointer">Testimonios</a>
            <a href="#contacto" className="px-6 py-2 bg-black text-white rounded-full text-sm hover:bg-gray-800 transition cursor-pointer whitespace-nowrap">Contactar</a>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <a href="#talleres" onClick={() => setIsMobileMenuOpen(false)} className="block text-base hover:text-gray-600 transition cursor-pointer py-2">Talleres</a>
              <a href="#metodologia" onClick={() => setIsMobileMenuOpen(false)} className="block text-base hover:text-gray-600 transition cursor-pointer py-2">Metodología</a>
              <a href="#testimonios" onClick={() => setIsMobileMenuOpen(false)} className="block text-base hover:text-gray-600 transition cursor-pointer py-2">Testimonios</a>
              <a href="#contacto" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center px-6 py-3 bg-black text-white rounded-full text-base hover:bg-gray-800 transition cursor-pointer whitespace-nowrap">Contactar</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pt-20">
        <div className="max-w-5xl mx-auto text-center space-y-6 sm:space-y-8">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 rounded-full">
            <i className="ri-lightbulb-line text-base sm:text-lg"></i>
            <span className="text-xs sm:text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Formación Corporativa Innovadora</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-tight px-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A3A2E' }}>
            TRANSFORMAMOS<br />
            EQUIPOS EN MOTORES DE CAMBIO
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            Talleres corporativos que despiertan o amplifican el potencial de tu equipo
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-4 px-4">
            <a href="#talleres" className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-white rounded-full font-medium hover:opacity-90 transition cursor-pointer whitespace-nowrap text-sm sm:text-base" style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#1A3A2E' }}>
              Explorar Talleres
            </a>
            <a href="#contacto" className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white rounded-full font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap text-sm sm:text-base" style={{ fontFamily: 'Inter, sans-serif', color: '#1A3A2E', border: '2px solid #1A3A2E' }}>
              Contactar Ahora
            </a>
          </div>
        </div>
      </section>

      {/* About KREAT Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12 text-center">
            <span className="text-xs sm:text-sm font-semibold text-gray-700 tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>QUIÉNES SOMOS</span>
          </div>
          
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6 mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Somos los que <strong>rompemos la formación cómoda</strong> para provocar <strong>impacto real</strong> en las organizaciones
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              Cada taller es una experiencia única: dinámica, vibrante, inteligente e impredecible. Revelamos el <strong>potencial que ya existe en el equipo</strong> a través de metodologías creativas y personalizadas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6">
            <div className="md:col-span-2 h-64 sm:h-80 rounded-2xl sm:rounded-3xl overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/4e7cecd10299a92b722389e92f12657d.png" 
                alt="Dinámica de grupo" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="md:col-span-3 h-64 sm:h-80 rounded-2xl sm:rounded-3xl overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/bf7836a6087463470f453637d0c9cd2e.jpeg" 
                alt="Momento de revelación creativa" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Talleres Section */}
      <section id="talleres" className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-8 sm:mb-12 gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className="text-black">NUESTRO CATÁLOGO</span>{' '}
                <span style={{ color: '#1A3A2E' }}>DE <strong>TALLERES</strong></span>
              </h2>
              <p className="text-gray-600 text-base sm:text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
                Cuatro programas transformadores adaptados a las necesidades específicas de tu empresa
              </p>
            </div>
            <div className="hidden lg:block px-6 py-2 bg-black text-white rounded-full text-sm font-medium whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
              Tailoring Empresarial
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-product-shop>
            {/* Taller 1 */}
            <article className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition group cursor-pointer">
              <div className="w-full h-56 sm:h-64 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/464f2efa4fdb84e8c34e96ca724f5286.jpeg" 
                  alt="El Arte de Negociar" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-300"
                />
              </div>
              <div className="p-5 sm:p-6 space-y-3 sm:space-y-4">
                <h3 className="text-lg sm:text-xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <strong>El Arte de Negociar</strong><br />Con y Sin Palabra
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Domina la negociación no verbal: lenguaje corporal, respiración, mirada, gestualidad y el poder del vestuario profesional
                </p>
                <div className="pt-3 sm:pt-4">
                  <span className="inline-block px-3 sm:px-4 py-2 bg-gray-100 rounded-full text-xs sm:text-sm font-medium">Comunicación No Verbal</span>
                </div>
              </div>
            </article>

            {/* Taller 2 */}
            <article className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition group cursor-pointer">
              <div className="w-full h-56 sm:h-64 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/e9bab12bdfc571f4300565316d0eea80.jpeg" 
                  alt="Emprendedores Corporativos" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-300"
                />
              </div>
              <div className="p-5 sm:p-6 space-y-3 sm:space-y-4">
                <h3 className="text-lg sm:text-xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  De Empleados Pasivos<br />a <strong>Emprendedores Corporativos</strong>
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Fomenta la adhesión a la cultura empresarial y desarrolla el espíritu emprendedor de tu team
                </p>
                <div className="pt-3 sm:pt-4">
                  <span className="inline-block px-3 sm:px-4 py-2 bg-gray-100 rounded-full text-xs sm:text-sm font-medium">Cultura Empresarial</span>
                </div>
              </div>
            </article>

            {/* Taller 3 */}
            <article className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition group cursor-pointer">
              <div className="w-full h-56 sm:h-64 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/f918386f57f255b8ec43a4f2ea88ff57.jpeg" 
                  alt="La Motivación" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-300"
                />
              </div>
              <div className="p-5 sm:p-6 space-y-3 sm:space-y-4">
                <h3 className="text-lg sm:text-xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <strong>La Motivación</strong><br />Micro Empresarios Internos
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Transforma a tus empleados en micro empresarios que impulsan el crecimiento desde dentro
                </p>
                <div className="pt-3 sm:pt-4">
                  <span className="inline-block px-3 sm:px-4 py-2 bg-gray-100 rounded-full text-xs sm:text-sm font-medium">Motivación Empresarial</span>
                </div>
              </div>
            </article>

            {/* Taller 4 */}
            <article className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition group cursor-pointer">
              <div className="w-full h-56 sm:h-64 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/7706d3d6489b410b65ff8a4349b3c2a0/0bce0ec7c59d6fc15a2be589b34383d3.png" 
                  alt="Creatividad" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-300"
                />
              </div>
              <div className="p-5 sm:p-6 space-y-3 sm:space-y-4">
                <h3 className="text-lg sm:text-xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <strong>Creatividad</strong><br />Gimnasio Mental
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Fortalece el músculo creativo de tu equipo para que sea capaz de transformar los retos en oportunidades
                </p>
                <div className="pt-3 sm:pt-4">
                  <span className="inline-block px-3 sm:px-4 py-2 bg-gray-100 rounded-full text-xs sm:text-sm font-medium">Pensamiento Creativo</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Metodología Section */}
      <section id="metodologia" className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start justify-between mb-12 sm:mb-16 gap-6">
            <div className="mb-4 md:mb-0">
              <span className="inline-block px-4 sm:px-6 py-2 border border-white rounded-full text-xs sm:text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                NUESTRA METODOLOGÍA
              </span>
            </div>
            <div className="md:w-2/3">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                PASOS QUE GENERAN <strong>IMPACTO REAL</strong>
              </h2>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Paso 1 */}
            <div className="group cursor-pointer">
              <div className="w-full h-56 sm:h-64 rounded-2xl sm:rounded-3xl overflow-hidden mb-4 sm:mb-6" style={{ backgroundColor: '#F5F3EF' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                    <i className="ri-presentation-line text-5xl sm:text-6xl text-gray-900"></i>
                  </div>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>EL "ROLLO"</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Introducción conceptual y marco teórico adaptado a tu realidad empresarial
                </p>
              </div>
            </div>

            {/* Paso 2 */}
            <div className="group cursor-pointer">
              <div className="w-full h-56 sm:h-64 rounded-2xl sm:rounded-3xl overflow-hidden mb-4 sm:mb-6" style={{ backgroundColor: '#F5F3EF' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                    <i className="ri-team-line text-5xl sm:text-6xl text-gray-900"></i>
                  </div>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>DINÁMICA DE GRUPO</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Experiencia práctica y vivencial donde el aprendizaje se hace tangible
                </p>
              </div>
            </div>

            {/* Paso 3 */}
            <div className="group cursor-pointer">
              <div className="w-full h-56 sm:h-64 rounded-2xl sm:rounded-3xl overflow-hidden mb-4 sm:mb-6" style={{ backgroundColor: '#F5F3EF' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                    <i className="ri-lightbulb-flash-line text-5xl sm:text-6xl text-gray-900"></i>
                  </div>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>CONCLUSIONES</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Integración de aprendizajes y diseño de plan de acción concreto
                </p>
              </div>
            </div>

            {/* Paso 4 */}
            <div className="group cursor-pointer">
              <div className="w-full h-56 sm:h-64 rounded-2xl sm:rounded-3xl overflow-hidden mb-4 sm:mb-6" style={{ backgroundColor: '#F5F3EF' }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                    <i className="ri-settings-3-line text-5xl sm:text-6xl text-gray-900"></i>
                  </div>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>TAILORING</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Adaptación completa a las necesidades específicas de tu empresa
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios Section */}
      <section id="testimonios" className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 bg-red-600 rounded-full"></div>
              <span className="text-xs sm:text-sm font-semibold text-gray-700 tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>LO QUE DICEN NUESTROS CLIENTES</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Experiencias que <strong>transforman</strong><br />equipos y resultados
            </h2>
            <p className="text-gray-600 text-base sm:text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              Testimonios reales de empresas que han vivido la experiencia KREAT
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Testimonio 1 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 space-y-3 sm:space-y-4">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-orange-400 text-base sm:text-lg w-5 h-5 flex items-center justify-center"></i>
                ))}
              </div>
              <h4 className="text-base sm:text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Transformación real del equipo
              </h4>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                El taller de negociación cambió completamente la forma en que nuestro equipo comercial se comunica. Los resultados fueron inmediatos y medibles.
              </p>
              <div className="flex items-center gap-3 sm:gap-4 pt-3 sm:pt-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm sm:text-lg font-bold">MG</span>
                </div>
                <div>
                  <p className="font-bold text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>María González</p>
                  <p className="text-gray-600 text-xs sm:text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Directora Comercial, TechCorp</p>
                </div>
              </div>
            </div>

            {/* Testimonio 2 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 space-y-3 sm:space-y-4">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-orange-400 text-base sm:text-lg w-5 h-5 flex items-center justify-center"></i>
                ))}
              </div>
              <h4 className="text-base sm:text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Metodología innovadora y efectiva
              </h4>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                La dinámica fue vibrante e impredecible. Cada miembro del equipo salió motivado y con herramientas prácticas para aplicar de inmediato.
              </p>
              <div className="flex items-center gap-3 sm:gap-4 pt-3 sm:pt-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm sm:text-lg font-bold">JR</span>
                </div>
                <div>
                  <p className="font-bold text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Javier Ruiz</p>
                  <p className="text-gray-600 text-xs sm:text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>CEO, Innovatech Solutions</p>
                </div>
              </div>
            </div>

            {/* Testimonio 3 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 space-y-3 sm:space-y-4">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-orange-400 text-base sm:text-lg w-5 h-5 flex items-center justify-center"></i>
                ))}
              </div>
              <h4 className="text-base sm:text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Creatividad desbloqueada
              </h4>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                El gimnasio de creatividad abrió perspectivas que no sabíamos que teníamos. Ahora resolvemos problemas de forma completamente diferente.
              </p>
              <div className="flex items-center gap-3 sm:gap-4 pt-3 sm:pt-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm sm:text-lg font-bold">LC</span>
                </div>
                <div>
                  <p className="font-bold text-xs sm:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Laura Castro</p>
                  <p className="text-gray-600 text-xs sm:text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Directora de Innovación, Grupo Empresarial</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contacto" className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-16 sm:py-0">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e122548cf99e94f6e954029e1f49a07f.jpg" 
            alt="Impulsa el cambio" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 sm:gap-12">
            <div className="text-center md:text-left">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 sm:mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <strong>impulsa el cambio</strong>
              </h2>
              <a href="mailto:smh@silviamanzana.com" className="inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 bg-white rounded-full hover:bg-gray-100 transition cursor-pointer group">
                <span className="text-base sm:text-lg font-medium" style={{ fontFamily: 'Inter, sans-serif', color: '#1A3A2E' }}>Solicitar Información</span>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center group-hover:opacity-90 transition" style={{ backgroundColor: '#1A3A2E' }}>
                  <i className="ri-arrow-right-line text-white text-lg sm:text-xl"></i>
                </div>
              </a>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-white text-lg sm:text-xl md:text-2xl font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Talleres personalizados que revelan<br />
                el potencial de tu organización
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-16 px-4 sm:px-6" style={{ backgroundColor: '#1A3A2E' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-16">
            {/* Columna 1 */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>KREAT</h3>
              <p className="text-gray-400 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Transformamos equipos en motores de cambio a través de <strong>talleres corporativos innovadores</strong> y personalizados.
              </p>
              <div className="pt-2">
                <a 
                  href="mailto:smh@silviamanzana.com" 
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Enviar email"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>smh@silviamanzana.com</span>
                </a>
              </div>
            </div>

            {/* Columna 2 */}
            <nav className="space-y-3 sm:space-y-4">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-300 tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>TALLERES</h4>
              <ul className="space-y-2">
                <li><a href="#talleres" className="text-gray-400 hover:text-white transition text-sm cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>Negociación</a></li>
                <li><a href="#talleres" className="text-gray-400 hover:text-white transition text-sm cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>Emprendimiento Corporativo</a></li>
                <li><a href="#talleres" className="text-gray-400 hover:text-white transition text-sm cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>Motivación y Creatividad</a></li>
              </ul>
            </nav>
          </div>

          <div className="border-t border-gray-700 pt-8 sm:pt-12">
            <a href="http://www.kreat.pro" target="_blank" rel="noopener noreferrer" className="block cursor-pointer hover:opacity-80 transition">
              <h2 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-center tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif', color: '#F5F3EF' }}>
                KREAT
              </h2>
            </a>
          </div>

          <div className="mt-6 sm:mt-8 text-center">
            <a href="http://www.kreat.pro" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition text-xs cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>
              © 2026 KREAT | C/Manel Farrés, 15 
08172 Sant Cugat del Vallès (Barcelona) | Todos los derechos reservados
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
